import React, { useEffect, useState } from "react";

import SectionTitle from "@app/components/3_modules/sections/SectionTitle";
import moment from 'moment';

import { ChevronUp, ChevronDown, Zap, Building } from "lucide-react";
import { useAuth } from '@app/contexts/AuthContext';
import { apiCall } from '@app/utils/apiCall';
import { useDossier, useDossierDispatch, DOSSIER_ACTIONS } from "@app/contexts/DossierContext";

import ButtonPrimary from "@app/components/1_elements/ButtonPrimary";
import InputText from "@app/components/2_widgets/inputs/InputText";
import SelectList from "@app/components/2_widgets/SelectList";

export default function ElectricConsumptionDetail({structureDataId, callbackUpdateStructureData}) {    
    const [displaying, setDisplaying] = useState(true);
    const [loaded, setLoaded] = useState(false);
    const [structureData, setStructureData] = useState({});
    const [isLoading, setIsLoading] = useState(false);

    const dossier = useDossier();
    const auth = useAuth();
    
    const dossierDispatch = useDossierDispatch();

    if (!loaded && structureDataId) {
        apiCall({
            method: "get",
            path: `/api/v1/structure_datas/${structureDataId}`,
            auth: auth,
            onSuccess: (response) => {
                setLoaded(true);
                setStructureData(response.data.structure_data);
            }
        });        
    }

    useEffect(() => {
        setLoaded(false);
    }, [structureDataId, dossier]);
    
    function updateElectricIndex(index, v) {    
        structureData.electric_index[index].kWh = Number(v);

        const formData = new FormData();
        formData.append("structure_data[electric_index]", JSON.stringify(structureData.electric_index))
        
        apiCall({
            method: "patch",
            path: `/api/v1/structure_datas/${structureData.id}`,
            auth: auth,
            data: formData,
            onSuccess: (response) => {
                const st = response.data.structure_data;
                setStructureData(st);
                // Update Structure Data in Dossier context
                dossierDispatch({
                    type: DOSSIER_ACTIONS.UPDATE_STRUCTURE_DATA,
                    payload: {structure_data: st }
                });
            }
        });            
    }

    function synchronizeEnedis(event) {
        event.preventDefault();

        setIsLoading(true);

        const formData = new FormData();
        formData.append("start_date", dossier.tax_return?.fiscal_starting_date);
        formData.append("end_date", dossier.tax_return?.fiscal_closing_date);

        apiCall({
            method: "post",
            path: `/api/v1/structure_datas/electric_index/${structureData.id}`,
            auth: auth,
            data: formData,
            onSuccess: (response) => {
                setStructureData(response.data.structure_data);
                dossierDispatch({
                    type: DOSSIER_ACTIONS.UPDATE_STRUCTURE_DATA,
                    payload: {structure_data: response.data.structure_data }
                });                
                setIsLoading(false);
            }
        });
    }

    function displayElectricIndex(electricMonth, index) {   
        const classCustomRow = electricMonth.type == "prorata" ? "custom-row success" : "custom-row";

        return (
            <div key={index} className={`row align-items-center ${classCustomRow}`}>
                <div className="col-9">
                    <span>{moment(electricMonth["month"], "YYYY-MM").format('MMMM YYYY')}</span>
                </div>
                <div className="col-2">
                    <InputText 
                        placeholder="0"
                        defaultValue={electricMonth["kWh"]}                    
                        callbackBlur={(error, v) => updateElectricIndex(index, v)}
                    />
                </div>
                <div className="col-1"><span>kWh</span></div>
            </div>
        )
    }

    /** Update start at invoice */
    function updateStartAtInvoice(value) {
        const formData = new FormData();
        formData.append("structure_data[invoice_start_at]", value)
        
        apiCall({
            method: "patch",
            path: `/api/v1/structure_datas/${structureData.id}`,
            auth: auth,
            data: formData,
            onSuccess: (response) => {
                const st = response.data.structure_data;
                setStructureData(st);
                // Update Structure Data in Dossier context
                dossierDispatch({
                    type: DOSSIER_ACTIONS.UPDATE_STRUCTURE_DATA,
                    payload: {structure_data: st }
                });
            }
        });
    }

    const startAtOptions = Array.from({ length: 28 }, (_, i) => ({ label: i + 1, value: i + 1 }));

    function displayContent() {
        if (!displaying)
            return;

        const end_at = moment(dossier.tax_return?.fiscal_closing_date).format("DD-MM-YYYY");
        const start_at = moment(dossier.tax_return?.fiscal_starting_date).format("DD-MM-YYYY");
        
        const electric = structureData?.electric_index && structureData.electric_index != "undefined" ? structureData.electric_index : [];
        const invoiceStartAt = structureData?.invoice_start_at;

        return (
            <div>
                <p>Site : <i>{structure.trade_name}</i> - PDL : <i>{structure.plm_number}</i> - SIRET : <i>{structure.siret}</i></p>
                <p className="custom-row"><b>Période de calcul</b> : {start_at} au {end_at}</p>
                <div className="row">
                    <div className="col-6">
                        <div className="mb-4 mt-4">
                            <ButtonPrimary 
                                label="Synchroniser avec ENEDIS" 
                                onClick={synchronizeEnedis} 
                                isLoading={isLoading}
                            />
                        </div>
                    </div>
                    <div className="col-6">
                        <div className="row mb-4 mt-4 justify-content-end">
                            <div className="col-4 text-end align-self-center">Début de Facture le </div>                            
                            <div className="col-3 align-self-center">
                                <SelectList
                                    options={startAtOptions}
                                    placeholder=""
                                    defaultValues={{label: invoiceStartAt, value: invoiceStartAt}}
                                    callbackOnChange={(obj) => updateStartAtInvoice(obj.value)}
                                />                                
                            </div>
                            <div className="col-2 align-self-center">du mois</div>                            
                        </div>
                    </div>
                </div>                
                {electric.map(displayElectricIndex)}
            </div>            
        )        
    }

    // Build structure datas tabs
    const tabs = dossier.structure_datas?.map((st_data) => ({eventKey: st_data.id, tabTitle: st_data.structure?.trade_name, icon: <Building />}));
    // Get relative structure global information
    const structure = structureData?.structure ?? {};

    return (
        <div className="page-section">            
            <SectionTitle
                icon={<Zap />}
                title={`Consommation Énergétique - ${structure.trade_name}`}
                onClick={() => setDisplaying(!displaying)}
                rightIcon={displaying ? <ChevronUp /> : <ChevronDown />}
                subtitle="Calculer la consommation d'énergie sur la période fiscale"
            />
            {displayContent()}
        </div>
    )
}